import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Main, MainBody, MainTop, MainTopTitle, WhiteBox } from '@/components/Layout'
import Resx from '@/components/Resx'
import { Button, Input, Label, Alert } from 'reactstrap'
import { ValidationBoundary, ValidationState, Validator } from '@/components/validation'
import GroupHeader from '@/components/reporting/submissions/groupHeader'
import ErrorBoundary from '@/components/ErrorBoundary'
import { ReportSubmissionGroupState } from '@/enums/ReportSubmissionGroupState'
import RsaSigner from '@/utils/RsaSigner'
import { PrivateKeyDropZone } from './dropzone'
import { allowErrorDialog } from '@/utils/fetch'
import context from '@/context'
import { Icon } from '@/components/Icon'
import SubmittedXmls from './submittedXmls'
import { useLocalization } from '@/hooks/localization'
import { openAlertDialog, openErrorDialog } from '@/utils/dialogHelpers'
import LoadingIndicator from '@/components/LoadingIndicator'
import { xmlValidationResult } from '@/enums/xmlValidationResult'
import { useSignalR } from '@/hooks/useSignalR'

const XmlSigning = React.memo(function XmlSigning ({ match }) {
  const [passphrase, setPassphrase] = useState('')
  const [privateKey, setPrivateKey] = useState(null)

  const [signing, setSigning] = useState(false)
  const [reload, setReload] = useState(false)
  const [statusCode, setStatusCode] = useState(null)
  const [fetching, setFetching] = useState(true)
  const [reportSubmissionGroup, setReportSubmissionGroup] = useState(null)
  const [reportSubmissionGroupState, setReportSubmissionGroupState] = useState(null)

  const { localize } = useLocalization()

  const id = useMemo(() => match.params.parentId, [match.params.parentId])

  const getReportSubmitionGroup = useCallback(() => {
    setFetching(true)
    context.api.reportSubmissionGroupService.get(id, allowErrorDialog)
      .then(response => {
        setReportSubmissionGroup(response)
        setReportSubmissionGroupState(response.state ? response.state : null)
        setFetching(false)
      })
      .catch(() => {
        setFetching(false)
      })
  }, [id])

  useEffect(() => {
    getReportSubmitionGroup()
  }, [id, getReportSubmitionGroup])

  useEffect(() => {
    if (reload) {
      getReportSubmitionGroup()
      setReload(false)
    }
  }, [id, reload, getReportSubmitionGroup])

  useEffect(() => {
    switch (statusCode) {
      case null:
        return
      case xmlValidationResult.enum.Success:
        openAlertDialog({ title: 'Reporting.XmlSigningTitle', text: 'Enums.XmlValidationResult_1' })
        break
      case xmlValidationResult.enum.None:
        openErrorDialog({ customTitle: 'Reporting.XmlSigningTitle', customErrorText: 'Enums.XmlValidationResult_0' })
        break
      case xmlValidationResult.enum.MissingValidPublicKeyForCurrentUser:
        openErrorDialog({ customTitle: 'Reporting.XmlSigningTitle', customErrorText: 'Enums.XmlValidationResult_2' })
        break
      case xmlValidationResult.enum.MissingValidSystemRsaKey:
        openErrorDialog({ customTitle: 'Reporting.XmlSigningTitle', customErrorText: 'Enums.XmlValidationResult_3' })
        break
      case xmlValidationResult.enum.InvalidSignature:
        openErrorDialog({ customTitle: 'Reporting.XmlSigningTitle', customErrorText: 'Enums.XmlValidationResult_4' })
        break
      case xmlValidationResult.enum.Failure:
        openErrorDialog({ customTitle: 'Reporting.XmlSigningTitle', customErrorText: 'Enums.XmlValidationResult_5' })
        break
    }
  }, [statusCode])

  const onFileAdded = useCallback(e => setPrivateKey(e), [])
  const onPassphraseChange = useCallback(e => setPassphrase(e.target.value), [])
  const privateKeyContent = useMemo(() => privateKey ? privateKey.content : null, [privateKey])

  const onClick = useCallback(() => {
    setSigning(true)
    context.api.reportSubmissionGroupService.getXmlForSigning(id, allowErrorDialog)
      .then(xml => {
        const signer = new RsaSigner(privateKeyContent, passphrase)
        signer.signXml(xml)
          .then(signedXml => context.api.reportSubmissionGroupService.submitXml({ xml: signedXml, reportSubmissionGroupId: id }, allowErrorDialog))
          .catch((err) => {
            setSigning(false)
            if (err) {
              openErrorDialog({ customErrorText: err.message, customTitle: 'Reporting.XmlSigningTitle' })
            }
          })
      })
      .catch(() => {
        setSigning(false)
        openErrorDialog({
          customErrorText: 'Reporting.ErrorWhileRetrievingXmlForSigning',
          customTitle: 'Reporting.XmlSigningTitle'
        })
      })
  }, [id, privateKeyContent, passphrase])

  useSignalR('xmlSigningFinished', async data => {
    const parsed = JSON.parse(data)
    if (reportSubmissionGroup && reportSubmissionGroup.id !== parsed.ReportInstanceGroupId) return reportSubmissionGroup

    setSigning(false)
    setReload(true)
    setStatusCode(parsed.StatusCode)
  }, [reportSubmissionGroup])

  const renderBody = useCallback(() => {
    if (reportSubmissionGroupState === ReportSubmissionGroupState.enum.Sent ||
      reportSubmissionGroupState === ReportSubmissionGroupState.enum.Accepted) { return null }
    if (reportSubmissionGroupState !== ReportSubmissionGroupState.enum.SignPending) { return <Alert><Resx name='Reporting.PleaseSubmitFirst' /></Alert> }

    return <ValidationBoundary>
      <div>
        <Label><Resx name='Security.RsaPrivateKeyPassphrase' /></Label>
        <Validator name='passphrase' value={passphrase} required>
          <Input type='password' value={passphrase} disabled={signing} onChange={onPassphraseChange} />
        </Validator>
      </div>
      <div>
        <Label><Resx name='Security.RsaPrivateKeyFile' /></Label>
        <Validator name='privateKey' value={privateKey} required>
          <PrivateKeyDropZone
            privateKey={privateKey}
            onFileLoaded={onFileAdded} />
        </Validator>
      </div>
      <ValidationState render={({ isValid }) =>
        <Button color='primary' disabled={!isValid || signing} onClick={onClick}>
          <Icon name={signing ? 'loader bx-spin' : 'save'} /> &nbsp; <Resx name='Reporting.SignAndSaveXml' />
        </Button>
      } />
    </ValidationBoundary>
  }, [onClick, onFileAdded, onPassphraseChange, passphrase, privateKey, reportSubmissionGroupState, signing])

  return fetching ? <LoadingIndicator /> : <Main>
    <MainTop>
      <MainTopTitle backUrl={`/report-submission-groups/${id}/report-submissions`}>
        <Resx name='Reporting.XmlSigningTitle' />
      </MainTopTitle>
    </MainTop>
    <MainBody transparent>
      <ErrorBoundary>
        <GroupHeader localize={localize} reportSubmissionGroup={reportSubmissionGroup} />
      </ErrorBoundary>
      <WhiteBox>
        {renderBody()}
        <SubmittedXmls
          dataSource={() => context.api.reportSubmissionGroupService.getSubmittedXmls(id)}
          downloadAction={context.api.reportSubmissionGroupService.downloadSubmittedXml}
        />
      </WhiteBox>
    </MainBody>
  </Main>
})

export default XmlSigning



// WEBPACK FOOTER //
// ./src/report-app/pages/reporting/submissions/xmlSigning/index.js